import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/** El evento con el que la tarjeta de Estado de la ficha abre una sección y la trae a la vista. */
const EVENTO_ABRIR_SECCION = 'atlas:abrir-seccion-de-ficha';

/**
 * Abre la sección `id` (si estaba plegada) y la desplaza a la vista. Lo usa la lista de
 * "lo que falta" de la ficha: tocar "Falta el precio" lleva al precio
 * (ADR-2026-10-06-la-ficha-ocupa-la-pantalla).
 */
export function irASeccion(id: string) {
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_SECCION, { detail: id }));
  // Un cuadro después: si estaba plegada, primero se tiene que abrir para medir bien.
  requestAnimationFrame(() =>
    document.getElementById(`seccion-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  );
}

/**
 * La card plegable de la ficha de un producto: ícono en un chip, título,
 * resumen cuando está cerrada y aviso en ámbar cuando le falta algo obligatorio.
 *
 * Vivía adentro de `FichaDeProducto.tsx`. Se extrajo cuando el editor de un post
 * del calendario de redes necesitó exactamente la misma card
 * (ADR-2026-09-15-el-post-se-edita-al-lado-del-calendario): copiarla era la
 * segunda versión de lo mismo, o sea el punto donde las dos empiezan a
 * divergir — el mismo motivo por el que se extrajo `ModalConfirmar`.
 *
 * El chip del ícono es del azul suave de Atlas en TODAS las secciones: un solo tono,
 * para que el color siga queriendo decir estado (el ámbar del aviso) y no "qué
 * sección es" (ADR-2026-10-06-la-ficha-ocupa-la-pantalla).
 */
export function Seccion({
  id,
  titulo,
  icono,
  resumen,
  aviso,
  plegable = true,
  abiertaPorDefecto,
  children,
}: {
  /** Para llegar a ella desde afuera con `irASeccion(id)`. */
  id?: string;
  titulo: string;
  icono: React.ReactNode;
  resumen?: string;
  /** Texto en ámbar: algo obligatorio de esta sección todavía falta. */
  aviso?: string | null;
  plegable?: boolean;
  abiertaPorDefecto?: boolean;
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(abiertaPorDefecto ?? !plegable);
  useEffect(() => {
    if (!id) return;
    const abrir = (e: Event) => {
      if ((e as CustomEvent<string>).detail === id) setAbierta(true);
    };
    window.addEventListener(EVENTO_ABRIR_SECCION, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_SECCION, abrir);
  }, [id]);
  const contenido = <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">{children}</div>;

  const cabecera = (
    <>
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
          aviso ? 'bg-amber-50 text-amber-600' : 'bg-[#EEF2FF] text-[#1644EB]'
        }`}
      >
        {icono}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-brand-black">{titulo}</span>
        {(aviso || (!abierta && resumen)) && (
          <span
            className={`mt-0.5 block truncate text-xs ${aviso ? 'text-amber-700' : 'text-neutral-400'}`}
          >
            {aviso ?? resumen}
          </span>
        )}
      </span>
    </>
  );

  return (
    <section
      id={id ? `seccion-${id}` : undefined}
      className={`scroll-mt-24 overflow-hidden rounded-2xl border bg-surface-card shadow-sm ${
        aviso ? 'border-amber-200' : 'border-neutral-200'
      }`}
    >
      {plegable ? (
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          aria-expanded={abierta}
          className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-neutral-50/60 sm:p-5"
        >
          {cabecera}
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform ${abierta ? 'rotate-180' : ''}`}
          />
        </button>
      ) : (
        <div className="flex items-center gap-3 p-4 sm:p-5">{cabecera}</div>
      )}
      {abierta && contenido}
    </section>
  );
}

export default Seccion;
